<template>
  <div :class="$setClasses">
    <template v-if="!listPaymentsInitLoading">
      <card-flex
        v-if="cardContent"
        :content="cardContent"
        :icon="cardIcon"
        :icon-green="true"
        :mobile-wrapper-column="true"
        class="step-comparator-wrapper__header__card-flex"
        no-box-shadow
      >
      </card-flex>
      <div v-if="errorMessage">
        <div :class="$vClass('error-message')">{{ errorMessage }}</div>
      </div>
    </template>
    <template v-if="stepConfig?.component">
      <component :is="stepConfig.component"
                  ref="stepPaymentRef"
                  v-loading="loading || installmentLoading"
                  :offer="offer"
                  :options-content="paymentOptions"
                >
      </component>
    </template>
    <template v-else>
      <div>
        <div v-if="content && !listPaymentsInitLoading"
             :class="[$vClass('content'), 'u-margin-top-default u-margin-bottom-default']"
             v-html="content"></div>
      </div>
      <list-payments-type
        v-if="listPaymentsType"
        data-testid="customer-payment-step"
        v-loading="loading"
        v-model:toggle="toggle"
        v-model:payment="modelPayment"
        :offer="offer"
        :list-payments-type="listPaymentsType"
        :text-params="textParams"
        :bank-data="offer?.failed_payment?.nextAction?.display_bank_transfer_instructions"
        :create-bank-transfer="createBankTransfer"
        :force-next-button-disabled="forceNextButtonDisabled"
        ref="listPaymentsType"
        @init-loading="onListPaymentsInitLoading"
        @payment-authenticated="paymentAuthenticated"
        @payment-error="paymentError"
        @token-change="tokenChange"
        @update:loading="setLoading"
        @next-action="nextAction"
      />
      <template v-if="!listPaymentsInitLoading">
        <p v-if="stripePaymentError" :class="$vClass('error')">
          {{ stripePaymentError }}
        </p>
        <card-flex
                v-if="footerCardContent && (!hasPaidArrhes || arrhesAlreadyPaid)"
                :class="[$vClass('memo'), 'u-border-none u-margin-top-default', 'step-comparator-wrapper__header__card-flex']"
                :content="footerCardContent"
                :icon="stepConfig.footerCard?.icon"
                :mobile-wrapper-column="true"
                icon-green
                no-box-shadow
              />
        <p v-if="displayNotice"
          :class="`${$vClass('card__notice')} u-margin-top-default`"
          v-html="getTextVersion('payment.creditCard.amountWarningText')"></p>
        <card-flex v-if="!hasPaidArrhes && displaySecurity"
                  :class="$vClass('security')"
                  :content="$t('payment.security.content')"
                  :icon="['fal', 'lock-alt']"
                  mobile-wrapper-column
                  no-box-shadow>
          <template v-slot:after>
            <div :class="$vClass('security__logos')">
              <img v-for="img in $tm('payment.security.logo')"
                  :alt="img.name"
                  :class="$vClass('security__logo')"
                  :key="img.image"
                  :src="img.image" />
            </div>
          </template>
        </card-flex>
      </template>
    </template>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import { i18n } from "@/plugins/i18n";
import { mapState, mapActions, mapWritableState } from "pinia";
import {
  useConfigStore,
  useProfileStore,
  useDimensionStore,
  useInstallmentStore,
  usePaymentsStore,
} from "@/stores";
import CardFlex from "@/utils/components/CardFlex.vue";
import CardPayment from "@/utils/components/CardPayment.vue";
import ListCardsPayment from "@/components/profile/payment/ListCardsPayment.vue";
import InstallmentConfirm from "@/components/payments/installment/InstallmentConfirm.vue";
import InstallmentPlan from "@/components/payments/installment/InstallmentPlan.vue";
import {
  useMoveManager,
  useMoveManagerEmits,
  useMoveManagerProps,
} from "@/composables/useMoveManager";
import { globalProperties } from "@nextories/vue-core-components/src/plugins/i18n";
import offersHelper from "@/utils/helpers/offers";

export default {
  name: "StepPayment",
  nameClassStyle: "step-payment",
  emits: useMoveManagerEmits,
  props: useMoveManagerProps,
  components: {
    CardFlex,
    CardPayment,
    ListCardsPayment,
    InstallmentPlan,
    InstallmentConfirm,
  },
  setup(props, { emit }) {
    const context = useMoveManager(props, { emit });
    context.generatedPrice = computed(() => {
      return props.offer && offersHelper.getPrice(props.offer, context.exclTax.value);
    });
    context.priceDiff = computed(() => {
      return (
        Math.round(
          (context.generatedPrice.value + context.servicesPrice.value - context.ceiling.value) *
            100,
        ) / 100
      );
    });
    // Stripe error handler
    context.stripePaymentError = ref(null);
    context.onStripeError = (errorCode) => {
      const errorPath = errorCode && `stripe.error_codes.${errorCode}`;
      if (errorPath) {
        context.stripePaymentError = globalProperties.$tOrFallback(
          errorPath,
          i18n.global.t("stripe.stripe_error_default_text"),
        );
      } else {
        context.stripePaymentError = errorCode;
      }
    };
    return context;
  },
  data() {
    return {
      postTokenAction: null,
      stripeToken: null,
      radioGroup: {
        creditCard: "creditCard",
        installment: "installment",
        bankTransfer: "bankTransfer",
      },
      modelPayment: "",
      toggle: "",
      listPaymentsInitLoading: false,
    };
  },
  computed: {
    ...mapWritableState(useConfigStore, ["stepLoading", "loading", "nextButtonDisabled"]),
    ...mapWritableState(useProfileStore, ["cardsPayment"]),
    ...mapWritableState(useInstallmentStore, ["installmentLoading"]),
    ...mapWritableState(usePaymentsStore, ["selectedPayment"]),
    ...mapState(useConfigStore, ["comparatorConfig", "isPrebooking"]),
    ...mapState(useDimensionStore, ["isMobile"]),
    ...mapState(useInstallmentStore, ["linkInstallmentPayment", "errorMessage"]),
    ...mapState(usePaymentsStore, ["paymentTypes"]),
    createBankTransfer() {
      return !!(!this.isPrebooking && !this.hasPaidArrhes);
    },
    paymentOptions() {
      return this.stepConfig?.options || {};
    },
    selectedPaymentConfig() {
      return this.paymentOptions.paymentTypes?.[this.modelPayment];
    },
    listPaymentsType() {
      const paymentTypes = this.paymentOptions.paymentTypes;
      if (!paymentTypes) return null;

      return Object.fromEntries(
        Object.entries(paymentTypes).filter(([, paymentType]) => paymentType?.display),
      );
    },
    content() {
      let content = this.selectedPaymentConfig?.content || this.stepConfig?.content;
      if (this.modelPayment === this.radioGroup.bankTransfer) {
        content = this.getTextVersionNotFirstBooking(content);
      }
      return this.getTextVersion(content, this.textParams);
    },
    displayNotice() {
      const displayNotice = this.selectedPaymentConfig?.displayNotice || this.paymentOptions.displayNotice;
      return typeof displayNotice === "boolean" ? displayNotice : true;
    },
    displaySecurity() {
      const displaySecurity = this.paymentOptions.displaySecurity;
      return typeof displaySecurity === "boolean" ? displaySecurity : true;
    },
    stepConfig() {
      return this.comparatorConfig.payment;
    },
    cardIcon() {
      return this.stepConfig?.card?.icon;
    },
    cardContent() {
      return this.getTextVersion(this.stepConfig?.card?.content, this.textParams);
    },
    footerCardContent() {
      let content = this.selectedPaymentConfig?.footerCard?.content || this.stepConfig?.footerCard?.content;
      if (this.modelPayment === this.radioGroup.bankTransfer) {
        content = this.getTextVersionNotFirstBooking(content);
      }
      return content ? this.getTextVersion(content, this.textParams) : "";
    },
    hasPaidArrhes() {
      return this.offer?.arrhes_payment?.status === "succeeded";
    },
    textParams() {
      const overrides = {};
      if (this.hasPaidArrhes || this.arrhesAlreadyPaid?.amountReceived) {
        const arrhesAmount =
          this.arrhesAlreadyPaid?.amountReceived || this.offer.arrhes_payment.amountReceived;
        overrides.arrhesAmount = this.$n(arrhesAmount || 0, "currency");
        overrides.solde = this.$n(this.calcPrice - arrhesAmount || 0, "currency");
      }
      return {
        ...this.getTextParams,
        ...overrides,
        isMobile: this.isMobile,
        priceDiff: this.$n(this.priceDiff || 0, "currency"),
        arrhesPercent: this.arrhesPercent,
        arrhesAlreadyPaid: this.arrhesAlreadyPaid,
        exclTax: this.exclTax ? "HT" : "TTC",
      };
    },
    forceNextButtonDisabled() {
      return !this.isPrebooking && !this.arrhesAlreadyPaid;
    },
  },
  created() {
    this.setStepLoading(false);
  },
  unmounted() {
    useInstallmentStore().$reset();
    this.cardsPayment = [];
    this.nextButtonDisabled = false;
    this.selectedPayment = '';
  },
  methods: {
    ...mapActions(useConfigStore, ["setLoading"]),
    ...mapActions(useInstallmentStore, ["postInstallmentPayment"]),
    getTextVersionNotFirstBooking(pathToText) {
      return `${pathToText}${(!this.demand?.is_first_booking && this.hasPaidArrhes ? '.notFirstBooking' : '.default')}`;
    },
    onListPaymentsInitLoading(loading) {
      this.listPaymentsInitLoading = loading;
    },
    nextStep(condition) {
      if (condition !== false) {
        this.$router.push({
          name: "booking",
          params: {
            id: this.$route.params.id,
          },
          hash: `#${this.comparatorConfig.payment.next.name}`,
        });
      }
    },
    async tokenChange(token, intent) {
      this.stripeToken = token || this.stripeToken;
      if (this.postTokenAction) {
        const result = await this.postTokenAction(
          this.stripeToken,
          intent,
          this.authenticatePayment,
          'creditCard',
        );
        this.toggle = "";
        this.nextStep(result);
      } else {
        this.nextStep();
      }
    },
    paymentAuthenticated(paymentIntent) {
      if (paymentIntent) {
        // TODO
        this.tokenChange(this.stripeToken, paymentIntent);
      } else {
        this.setLoading(false);
      }
      // if (this.isBookingCharge && paymentIntent) {
      //   this.book(null, paymentIntent);
      // }
    },
    async submitBankTransfer() {
      const result = await this.postTokenAction(null, null, null, 'bankTransfer');
      if (result) {
        this.nextStep(true);
      } else {
        this.setLoading(false);
      }
    },
    async submitPayment() {
      if (this.modelPayment === this.radioGroup.installment) {
        await this.redirectToInstallment();
      } else if (this.modelPayment === this.radioGroup.bankTransfer) {
        await this.submitBankTransfer();
      } else {
        // WE ARE ALREADY REGISTERED ON STRIPE
        if (this.$refs?.listPaymentsType?.cardPayment?.submitFormToCreateToken && this.toggle === 'addCreditCard') {
          this.$refs.listPaymentsType.cardPayment.submitFormToCreateToken();
        } else {
          await this.tokenChange(null, null);
        }
      }
    },
    async redirectToInstallment() {
      try {
        this.installmentLoading = true;
        if (
          !this.offer?.floa_payment?.dealReference &&
          this.paymentTypes.length === 0
        ) {
          this.$router.push({
            name: "comparator",
          });
          return;
        }
        if (this.projectSelected?.token) {
          await this.postInstallmentPayment({
            quoteId: this.offer?.id ?? "",
            token: this.projectSelected?.token ?? "",
          });
          window.location.href = this.linkInstallmentPayment?.href ?? "";
        }
      } catch (error) {
        this.installmentLoading = false;
        this.setLoading(false);
      }
    },
    async startPlanInstallmentPayment() {
      try {
        this.setLoading(true);
        await this.book(null, null, null, 'installment');
        this.nextStep(true);
      } catch (error) {
        console.error(error);
        this.setLoading(false);
      }
    },
    async bookAction() {
      if (this.offer?.floa_payment?.dealReference) {
        this.startPlanInstallmentPayment();
      } else {
        this.setLoading(true);
        this.postTokenAction = this.book;
        this.submitPayment();
      }
    },
    async prebookAction() {
      this.setLoading(true);
      if (!this.floaAlreadyPaid) {
        this.postTokenAction = this.preBook;
        this.submitPayment();
      } else {
        const result = await this.preBook(null, null, null, this.modelPayment);
        if (result) {
          this.nextStep(true);
        } else {
          this.setLoading(false);
        }
      }
    },
    nextAction() {
      if (this.stepConfig?.next.action) {
        this[this.stepConfig?.next.action]();
      }
    },
    authenticatePayment(intent) {
      if (this.$refs?.listPaymentsType.cardPayment) {
        return this.$refs.listPaymentsType.cardPayment.authenticatePayment(intent);
      }
      return false;
    },
    paymentError(error) {
      console.log(error);
      this.$helpers.notificationWrapper("stripe", error?.code);
    },
    setLoading(value) {
      if (value) {
        this.stripePaymentError = null;
      }
      this.loading = value;
    },
    setStepLoading(value) {
      this.stepLoading = value;
    },
  },
  watch: {
    modelPayment(value) {
      this.selectedPayment = value;
      this.nextButtonDisabled = !!(value === 'bankTransfer' && this.forceNextButtonDisabled);
    },
  },
};
</script>

<style scoped lang="scss">
.step-payment {
  .--margin-bottom-0 {
    margin-bottom: 0;
  }

  &__error-message {
    color: var(--nxt-color-danger);
    font-size: 16px;
    font-weight: 600;
  }

  &__subtitle {
    display: inline-block;
    margin-right: 10px;
  }

  &__subtitle-legend {
    display: inline-block;
    text-transform: uppercase;
  }

  &__wrapper {
    padding: {
      top: 20px;
      bottom: 20px;
    }
  }

  &__card {
    &__notice {
      font-size: 14px;
      font-style: italic;
    }
  }

  &__security {
    margin-top: 20px;
    border: solid var(--nxt-color-primary) 1px;

    &__logos {
      display: flex;
      gap: 14px;
    }

    &__logo {
      height: 25px;
    }
  }

  &__error {
    padding-bottom: 15px;
    color: var(--nxt-color-danger);
    font-size: 16px;
    font-weight: 600;
  }
}
</style>

<style lang="scss">
.step-comparator-wrapper.booking.--payment {
  .step-comparator-wrapper__footer {
    &__before {
      display: none;
    }

    &__after {
      justify-self: center;

      button {
        height: 41.5px;
        width: 220px;

        .fa-angle-right {
          display: none;
        }
      }
    }

    @include breakpoint(tablet) {
      &__before {
        display: block;
      }

      &__after {
        justify-self: end;

        button {
          height: auto;
          width: auto;

          .fa-angle-right {
            display: inline;
          }
        }
      }
    }
  }
}
</style>
